import React, { useState } from 'react';
import { ChatbotMessage, isLandscapeCard } from '../../models/chatbot/types';
import { AiMarkdown, ReferenceCard } from '../ai-content';
import AiResponseActions from '../ai-content/AiResponseActions';
import { Button, Icon } from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import {
  trackChatbotReferenceClick,
  trackChatbotReferencesExpand,
  trackChatbotLinkClick,
} from '../../models/assistant/analytics/analytics';

const MAX_VISIBLE_REFERENCES = 3;

interface MessageBubbleProps {
  message: ChatbotMessage;
  isStreaming?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isStreaming = false,
}) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const displayContent = message.content || '';
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(false);

  const hasReferences = message.references && message.references.length > 0;
  const shouldCollapseReferences =
    message.references && message.references.length > MAX_VISIBLE_REFERENCES;
  const displayedReferences =
    shouldCollapseReferences && !isReferencesExpanded
      ? message.references?.slice(0, MAX_VISIBLE_REFERENCES) || []
      : message.references || [];
  const hasLandscapeCards = (message.references || []).some(
    (ref) => ref.card && isLandscapeCard(ref.card)
  );

  const formatTime = (date: string) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getISODateTime = (date: string) => {
    return new Date(date).toISOString();
  };

  return (
    <div
      className={`chatbot__message ${
        isUser ? 'chatbot__message--user' : 'chatbot__message--assistant'
      } ${isStreaming ? 'chatbot__message--streaming' : ''}`}
      data-testid={
        isUser
          ? 'playwright-chatbot-user-message'
          : 'playwright-chatbot-assistant-message'
      }
    >
      <div
        className={`chatbot__message-bubble ${
          isUser
            ? 'chatbot__message-bubble--user'
            : 'chatbot__message-bubble--assistant'
        }`}
      >
        <div className="chatbot__message-content">
          {isUser ? (
            <div className="chatbot__message-text">{displayContent}</div>
          ) : (
            <AiMarkdown
              content={displayContent}
              onLinkClick={(url: string, isInternal: boolean) =>
                trackChatbotLinkClick({
                  thread_id: message.threadId,
                  url,
                  is_internal: isInternal,
                })
              }
            />
          )}
          {hasReferences && (
            <nav aria-label={t('chatbot.references.title')}>
              <div
                className={`ai-content__references ${
                  shouldCollapseReferences && !isReferencesExpanded
                    ? 'ai-content__references--collapsed'
                    : ''
                } ${
                  hasLandscapeCards
                    ? 'ai-content__references--has-landscape'
                    : ''
                }`}
              >
                {displayedReferences.map((reference) => (
                  <ReferenceCard
                    key={`${reference.referenceType}-${reference.referenceId}`}
                    referenceType={reference.referenceType}
                    referenceId={reference.referenceId}
                    card={reference.card}
                    onClick={() =>
                      trackChatbotReferenceClick({
                        thread_id: message.threadId,
                        reference_type: reference.referenceType,
                        reference_id: reference.referenceId,
                      })
                    }
                  />
                ))}
              </div>
              {shouldCollapseReferences && (
                <div className="ai-content__references-toggle">
                  <Button
                    variant="text"
                    size="compact"
                    onClick={() => {
                      if (!isReferencesExpanded) {
                        trackChatbotReferencesExpand({
                          thread_id: message.threadId,
                          reference_count: message.references?.length || 0,
                        });
                      }
                      setIsReferencesExpanded(!isReferencesExpanded);
                    }}
                    rightIcon={
                      <Icon>
                        {isReferencesExpanded ? 'expand_less' : 'expand_more'}
                      </Icon>
                    }
                    fullWidth={true}
                    label={
                      isReferencesExpanded
                        ? t('chatbot.references.see_less')
                        : t('chatbot.references.see_more', {
                            count: message.references?.length || 0,
                          })
                    }
                    className="ai-content__references-toggle-button"
                  />
                </div>
              )}
            </nav>
          )}
        </div>
        {!isStreaming && (
          <div className="chatbot__message-footer">
            {!isUser && (
              <AiResponseActions
                copy={message.copy}
                isStreaming={isStreaming}
                className="chatbot__message-actions"
                pendoId="chatbot-response-actions"
              />
            )}
            <time
              className={`chatbot__message-timestamp ${
                isUser
                  ? 'chatbot__message-timestamp--user'
                  : 'chatbot__message-timestamp--assistant'
              }`}
              dateTime={getISODateTime(message.createdAt)}
            >
              {formatTime(message.createdAt)}
            </time>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
